/**
 * Struktur des Board-Inhalts im Yjs-Dokument.
 *
 * Wichtig: Diese Interfaces beschreiben die *Sicht* auf ein Element, nicht die
 * Speicherform. Im Yjs-Dokument ist jedes Element ein eigenes `Y.Map`, damit
 * gleichzeitige Änderungen an verschiedenen Feldern (A verschiebt, B skaliert)
 * beide überleben. Ein JSON-Blob als Wert wäre Last-Write-Wins.
 */
export const Y_ELEMENTS = 'elements';
export const Y_FRAMES = 'frames';
export const Y_META = 'meta';
export const DEFAULT_IFRAME_VIEWPORT = 1440;
export const DEFAULT_PAGE_FACTOR = 3;
/** Wie viele Fensterhöhen der eingebetteten Seite bereitgestellt werden. */
export const PAGE_FACTORS = [1, 2, 3, 4, 6, 8];
/** Gängige Fensterbreiten für eingebettete Websites. */
export const IFRAME_VIEWPORT_PRESETS = [
    { width: 1920, label: 'viewport.monitor' },
    { width: 1440, label: 'viewport.desktop' },
    { width: 1280, label: 'viewport.laptop' },
    { width: 1024, label: 'viewport.tabletLandscape' },
    { width: 834, label: 'viewport.tablet' },
    { width: 390, label: 'viewport.phone' },
];
export const DEFAULT_TEXT_COLOR = '#f2eee6';
/** Zeilenabstand, wenn keiner gewählt wurde. */
export const DEFAULT_LINE_HEIGHT = 1.25;
/*
 * Grenzen für die frei eingegebenen Maße am Text.
 *
 * Sie sind weit gesteckt: Eine Schriftgröße steht in Board-Einheiten, und auf
 * einer unendlichen Fläche ist 600 für eine Titelzeile eine vernünftige Zahl.
 * Die Grenzen fangen nur ab, was kein Maß mehr ist — eine 0 aus einem halb
 * geleerten Eingabefeld oder eine Zahl, die das Layout sprengt.
 */
export const MIN_FONT_SIZE = 4;
export const MAX_FONT_SIZE = 2000;
export const MIN_LINE_HEIGHT = 0.5;
export const MAX_LINE_HEIGHT = 5;
export const DEFAULT_BACKGROUND = '#0b0b0d';
/**
 * Kuratierte Hintergrundtöne. Bewusst wenige und aufeinander abgestimmte: die
 * Inhalte sollen wirken, nicht der Untergrund. Eigene Farben sind über den
 * Farbwähler trotzdem möglich.
 */
export const BACKGROUND_PRESETS = [
    { value: '#0b0b0d', name: 'background.black' },
    { value: '#17181c', name: 'background.anthracite' },
    { value: '#101725', name: 'background.night' },
    { value: '#1c1a18', name: 'background.warmgrey' },
    { value: '#3a3d42', name: 'background.slate' },
    { value: '#e8e2d6', name: 'background.sand' },
    { value: '#f4f1ea', name: 'background.paper' },
    { value: '#ffffff', name: 'background.white' },
];
/**
 * Wahrgenommene Helligkeit einer Hex-Farbe (0…1), nach den WCAG-Koeffizienten.
 *
 * Wird gebraucht, um Raster und Frame-Rahmen auf hellen Hintergründen
 * umzudrehen — ein weißes Punktraster auf Papierweiß wäre unsichtbar.
 */
export function luminance(hex) {
    const value = hex.replace('#', '');
    const full = value.length === 3
        ? value
            .split('')
            .map((c) => c + c)
            .join('')
        : value;
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    if ([r, g, b].some(Number.isNaN))
        return 0;
    const channel = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
export const isLightBackground = (hex) => luminance(hex) > 0.4;
/**
 * „Automatisch": Der Wert wird aus der Größe des Frames abgeleitet und wächst
 * beim Skalieren mit. Ein fest eingetragener Wert täte das nicht — und ein
 * Raster, das nach dem Vergrößern nicht mehr passt, ist schlimmer als keines.
 */
export const FRAME_LAYOUT_AUTO = -1;
export const FRAME_FORMATS = [
    { key: 'wide', ratio: 16 / 9, labelKey: 'frame.format.wide', short: '16:9' },
    { key: 'classic', ratio: 4 / 3, labelKey: 'frame.format.classic', short: '4:3' },
    { key: 'square', ratio: 1, labelKey: 'frame.format.square', short: '1:1' },
    { key: 'post', ratio: 4 / 5, labelKey: 'frame.format.post', short: '4:5' },
    { key: 'story', ratio: 9 / 16, labelKey: 'frame.format.story', short: '9:16' },
];
/**
 * Wie viel des Blickfelds ein neu angelegter Frame einnimmt.
 *
 * Nicht die volle Fläche: Sonst liegt die Beschriftung des Frames über dem
 * oberen Bildrand und ist weder sicht- noch anfassbar.
 */
export const FRAME_PLACEMENT_FILL = 0.86;
/** Luft zwischen Auswahl und dem Frame, der darum gelegt wird. */
export const FRAME_AROUND_PADDING = 0.08;
export const DEFAULT_FRAME_STYLE = {
    background: '',
    borderColor: '#e8622e',
    borderWidth: 0,
    padding: FRAME_LAYOUT_AUTO,
    columns: FRAME_LAYOUT_AUTO,
    rows: FRAME_LAYOUT_AUTO,
};
/**
 * Hat der Frame eine sichtbare Gestaltung?
 *
 * Entscheidet, ob er auch für Gäste und im Vortrag gezeichnet wird: ein Frame
 * mit Hintergrund ist Inhalt, ein Frame ohne ist bloß Autorenstruktur.
 */
export const frameIsStyled = (frame) => frame.background !== '' || frame.borderWidth > 0;
/**
 * Farbpalette für Cursor-Zuweisung; deterministisch über die Teilnehmer-ID.
 *
 * Zwölf Töne statt acht: bei acht landen schon zwei Gäste mit rund 12 %
 * Wahrscheinlichkeit auf derselben Farbe, und dann sind ihre Cursor nur noch am
 * Namensschild zu unterscheiden.
 */
export const PRESENCE_COLORS = [
    '#e8622e',
    '#2e8ce8',
    '#22a06b',
    '#b45cd6',
    '#d6a127',
    '#e0457b',
    '#4b63d6',
    '#17a2b8',
    '#7cb342',
    '#f0654f',
    '#8e7cc3',
    '#00897b',
];
/**
 * Farben des Zeigers im Vortrag.
 *
 * Wenige und kräftige: Der Zeiger muss auf hellem wie auf dunklem Untergrund
 * sofort auffallen, und wer im Vortrag eine Farbe sucht, hat keine Zeit für
 * einen Farbkreis. Das klassische Laserrot steht bewusst zuerst.
 */
export const POINTER_COLORS = ['#ff2d2d', '#ffd23f', '#2eda8c', '#37a6ff', '#ffffff'];
export const DEFAULT_POINTER_COLOR = POINTER_COLORS[0];
export function colorForId(id) {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
        hash = (hash * 31 + id.charCodeAt(i)) | 0;
    }
    const index = Math.abs(hash) % PRESENCE_COLORS.length;
    return PRESENCE_COLORS[index] ?? PRESENCE_COLORS[0];
}
//# sourceMappingURL=board.js.map